import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import { saveLastBidAreas } from '~/components/bidding/common/lastBidArea'
import { useCreateBiddingIntradayOrder } from '~/hooks/bidding/useCreateBiddingIntradayOrder'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { now as serverNow } from '~/models/clock'
import { useGetApprovalFlowScreen } from '~/pages/AdminApprovalFlow/hooks/useGetApprovalFlowScreen'
import type { IntradayBidCreateResult } from '~/pages/IntradayBidding/components/IntradayBidCreateModal'
import { gcStatusOfKoma } from '~/pages/IntradayBidding/models/deliveryDate'
import { calcGcAtForKoma } from '~/pages/IntradayBidding/models/gcCountdown'
import {
  isIntradayApprovalFlowUnresolved,
  resolveIntradayApprovalMode,
} from '~/pages/IntradayBidding/models/intradayApprovalMode'
import { translateIntradayBidCreateError } from '~/pages/IntradayBidding/models/intradayBidCreateError'
import { buildCreateIntradayOrderRequest } from '~/pages/IntradayBidding/models/intradayOrderWrite'

type Params = {
  /** 対象受渡日。 GC 判定と `gcAt` 算出の基準日。 */
  targetDate: Date
  /** `targetDate` の proto 表現 (呼び出し側が既に持っているものを渡す)。 */
  targetDateFilter: ProtoDate
}

/**
 * 時間前入札の**札作成 (create) の確定処理**。 入札追加タブと板発注画面で共有する。
 *
 * 元は `tabs/BidCreateTab.tsx` の `handleCreateSubmit` に閉じていたが、 板発注画面の
 * クイック入札 (spec AC-BIN-027 / 028) が同じ確定処理を要るため hook へ出した。
 * **挙動は移設前と同一**で、 特に次の 2 つのガードを 2 画面で共有することが目的:
 *
 * - **承認フロー設定の取得中・取得失敗ブロック** (2026-09-16 決定): 対象エリアの
 *   承認フロー設定 (`useGetApprovalFlowScreen`) が読めていない間は作成を確定させない。
 *   `BidCreateTab` の個別作成ボタンは既に非活性にしているが、 `MarketBoardTab` の
 *   クイック入札ボタンは対象外 (板発注画面の主目的を承認フロー設定の可否で止めない
 *   判断) なので、 ここでの弾き直しが両画面の唯一の防波堤になる。
 * - **GC 再判定**: modal を開いたままコマ / 日付をまたぐと選択済のコマが GC を通過し
 *   得る (AC-BIN-014 でコマ select の選択肢は開いた時点で固定され closed を除外するが、
 *   既に選択済のフォーム値は自動ではクリアされない)。 確定時点の GC 状態を canonical な
 *   GC 時刻直接比較 (`gcStatusOfKoma`) で弾き直す (= `gcBoundarySlot` のコマ番号比較だと
 *   境界コマ自身の通過や未来受渡日のコマ 1/2 の通過を見逃す)。
 *   **backend 側の検査は代替にならない**: `intraday_order.go` は `now >= gcAt` を
 *   `FailedPrecondition` で拒むが、 その `gcAt` は request 入力値そのもの (サーバー側導出は
 *   #5006 で追跡中) なので「明らかに過去のコマ」しか塞げず、 `gcAt` が
 *   `(targetDate, timeCode)` と整合しているかは検証されない。 端末 TZ が JST 以外だと
 *   `calcGcAtForKoma` の算出値が最大 9 時間ずれる (`models/gcCountdown.ts` 参照) ため、
 *   改竄が無くても不正な `gcAt` は成立する
 *
 * ガードが 2 画面に複製されると、 一方だけ直して他方が抜ける形で実損に直結するため、
 * **板発注側で同等処理を書き写さない**こと。
 *
 * 作成後の一覧反映は呼び出し側で何もしなくてよい:
 * `useCreateBiddingIntradayOrder` が `biddingQueryKeys.intradayList` を invalidate し、
 * 板発注画面の「自社入札」 も同じ query key で引いているため自動で最新化される。
 *
 * @returns 確定処理。 **成功したら true** (呼び出し側はこれを見て modal を閉じる)。
 *   ガードで弾いた場合・RPC が失敗した場合は false で、 いずれも toast は本 hook が出す。
 */
export const useIntradayBidCreateSubmit = ({
  targetDate,
  targetDateFilter,
}: Params): ((result: IntradayBidCreateResult) => Promise<boolean>) => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  const { mutateAsync: createAsync } = useCreateBiddingIntradayOrder()
  // 承認モードは対象エリアの承認フロー設定から解決する (AC-BIN-094 / #6650)。
  // 取得中・取得失敗の間はこれまでの固定表示と同じフォールバックにする
  // (`resolveIntradayApprovalMode` 参照)。 板発注のクイック入札もこの hook を経由する
  // ため、 ここで解決すれば 2 画面で同じ挙動になる (write seam を分けない理由と同型)。
  const {
    data: approvalFlowScreenData,
    isLoading: isApprovalFlowScreenLoading,
    isError: isApprovalFlowScreenError,
  } = useGetApprovalFlowScreen({ throwOnError: false })

  return async (result: IntradayBidCreateResult): Promise<boolean> => {
    // 2026-09-16 決定: 呼び出し側 (BidCreateTab の個別作成ボタン、 MarketBoardTab の
    // クイック入札ボタン) は承認フロー設定の取得中・取得失敗の間、 導線自体を非活性に
    // しているが、 modal を開いたまま設定取得の状態が変わるレースを塞ぐため確定処理
    // 側でも弾く (= フォールバック `AUTO` で要承認の入札を作成できてしまう経路を
    // 残さない、 codex 指摘 2026-09-15 対応)。 判定は `BidCreateTab` の導線非活性と
    // 同じ `isIntradayApprovalFlowUnresolved` に揃える (= `data === undefined` のまま
    // 進む窓も塞ぐ)。
    if (
      isIntradayApprovalFlowUnresolved({
        processes: approvalFlowScreenData?.processes,
        isLoading: isApprovalFlowScreenLoading,
        isError: isApprovalFlowScreenError,
      })
    ) {
      showErrorToast({
        title: t('page:biddingIntraday.bidCreate.approvalFlowUnavailable'),
      })
      return false
    }
    if (gcStatusOfKoma(serverNow(), targetDate, result.timeCode) === 'closed') {
      showErrorToast({
        title: t('page:biddingIntraday.bidCreate.gcExceeded'),
      })
      return false
    }
    try {
      // 数量 (MW 入力) の MWh 換算は write seam (buildCreateIntradayOrderRequest) に閉じる。
      // gc_at は Create 必須 (= GC ガード)。 コマ開始 1 時間前を canonical 関数で算出
      // (= 日跨ぎ正しく、 コマ 1/2 は前日 23:00/23:30)。
      // 承認モードは対象エリアの承認フロー設定から解決する (AC-BIN-094 / #6650)。
      const approvalMode = resolveIntradayApprovalMode({
        processes: approvalFlowScreenData?.processes,
        isLoading: isApprovalFlowScreenLoading,
        isError: isApprovalFlowScreenError,
        areaCode: result.areaCode as AreaCode,
      })
      await createAsync(
        buildCreateIntradayOrderRequest({
          targetDate: targetDateFilter,
          areaCode: result.areaCode as AreaCode,
          timeCode: result.timeCode,
          direction: result.side === 1 ? Side.BUY : Side.SELL,
          // 発注主体はモーダルで選んだものを札へ載せる (issue #5366)。
          issuerType: result.issuerType,
          price: result.price,
          volumeMw: result.volumeMw,
          gcAt: timestampFromDate(calcGcAtForKoma(targetDate, result.timeCode)),
          approvalMode,
          // 自動取消時刻 (AC-BIN-017)。 値域検証は modal 側 (validateWithdrawAt)、
          // Timestamp 化は write seam に閉じる (#5007)。
          withdrawAtLocal: result.withdrawAt,
        }),
      )
      saveLastBidAreas('intraday', [result.areaCode as AreaCode])
      showSuccessToast({
        title: t('page:biddingIntraday.bidCreate.success', {
          koma: result.timeCode,
          side: t(
            result.side === 1
              ? 'page:biddingSpot.side.buy'
              : 'page:biddingSpot.side.sell',
          ),
          volume: Number(result.volumeMw),
        }),
      })
      return true
    } catch (err) {
      // AC-057: GC 超過 / その他 の 2 分岐固定文言のみを表示する (内部処理のシステム
      // メッセージを文言に連結しない)。
      showErrorToast({
        title: translateIntradayBidCreateError(err, t),
      })
      return false
    }
  }
}
